<template class="page-content">
  <ModalLayout ref="modal"
    modal-id="sessionTimeoutModal"
    :loading="false"
    :show-close="false"
    modal-style="background-color: rgba(0, 0, 0, 0.5)">
    <template #modal-header>
      <h5>Inactivity Warning</h5>
    </template>
    <template #modal-body>
      <p>
        Your session will expire due to inactivity in <strong>{{ countdown }}</strong>.
      </p>
      <p>
        Please select “Continue Session” to extend your session or select “Log Out” to log out now.
        Unsaved changes will not be saved if you choose to log out.
      </p>

      <hr>
      <button type="button" class="btn btn-primary mr-2" :disabled="extending" @click="continueSession">Continue Session</button>
      <button type="button" class="btn btn-secondary" @click="logOut">Log Out</button>
    </template>
  </ModalLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
// @ts-ignore
import ModalLayout from "@/src/components/shared/ModalLayout.vue";
// @ts-ignore
import {useSessionStore} from "@/src/stores/session";
import {SessionTimeoutManager, useSessionTimeoutManager} from "@/src/services/session";
import {FaviconBadger} from "@/src/services";

// Warns the user before their session times out due to inactivity. SessionTimeoutManager keeps every tab in
// sync, so continuing or logging out in one tab applies to every tab.
const sessionStore = useSessionStore();
const faviconBadger = new FaviconBadger();

const modal = ref<any>(null);
const extending = ref(false);

const countdown = computed(() => {
  const seconds = Math.max(sessionStore.secondsRemaining, 0);
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
});

watch(() => sessionStore.isWarningModalVisible, (visible) => {
  // @ts-ignore
  $(modal.value.$el).modal(visible ? 'show' : 'hide');
  if (visible) {
    startTitleBlink();
    // Shows a red circle on the favicon too, so the warning stands out even when the tab title is cut off
    faviconBadger.showAlert();
  } else {
    stopTitleBlink();
    faviconBadger.hideAlert();
  }
});

// Flashes the browser tab title while the warning is open so users on another tab notice it.
let originalTitle: string | null = null;
let titleBlinkInterval: ReturnType<typeof setInterval> | null = null;

function startTitleBlink() {
  if (titleBlinkInterval) return;

  originalTitle = document.title;
  let showWarning = true;
  titleBlinkInterval = setInterval(() => {
    document.title = showWarning ? `⚠️ Session expiring in ${countdown.value}` : originalTitle!;
    showWarning = !showWarning;
  }, 1000);
}

function stopTitleBlink() {
  if (titleBlinkInterval) {
    clearInterval(titleBlinkInterval);
    titleBlinkInterval = null;
  }
  if (originalTitle !== null) {
    document.title = originalTitle;
    originalTitle = null;
  }
}

async function continueSession() {
  extending.value = true;
  try {
    await useSessionTimeoutManager().refreshSession();
  } finally {
    extending.value = false;
  }
}

function logOut() {
  useSessionTimeoutManager().logout();
}

// Bootstrap removes body.modal-open when any modal hides, which stops a modal still open underneath the warning
// from scrolling. Put it back if another modal is still showing.
function restoreModalOpen() {
  // @ts-ignore
  if ($('.modal.show').length) $('body').addClass('modal-open');
}

onMounted(() => {
  // @ts-ignore
  $(modal.value.$el).on('hidden.bs.modal', restoreModalOpen);

  const config = SessionTimeoutManager.config();
  if (!config) return;

  useSessionTimeoutManager().start(config);
});

onBeforeUnmount(() => {
  // @ts-ignore
  $(modal.value.$el).off('hidden.bs.modal', restoreModalOpen);
  stopTitleBlink();
  useSessionTimeoutManager().destroy();
});
</script>

<style>
/* Keep the warning in front of every other modal (z-index 1050) and the side nav and overlays (up to 10000) */
#sessionTimeoutModal {
  z-index: 10050 !important;
}
</style>
